/**
 * ComplianceTab — per-account event-based compliance toggles on the
 * Investor Account Details page. Currently ships one rule (Form InVI
 * with RBI). Reads:
 *   GET /api/investor-accounts/{id}/compliances
 *   PUT /api/investor-accounts/{id}/compliances/{code}
 *
 * Toggling a rule ON tells the platform to auto-email the AMC's
 * Compliance Recipients list every time the rule's event fires (see
 * `compliance_service.py`). Turning it OFF halts those emails.
 *
 * The bottom card lists the last few compliance events (drawdown
 * receipts approved) so admins can see the trail without hunting
 * through emails.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, ShieldCheck, History as HistoryIcon, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ComplianceTab = ({ accountId }) => {
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);          // rule code currently being flipped
  const [remark, setRemark] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/investor-accounts/${accountId}/compliances`);
      setRules(data.compliances || []);
      setEvents(data.events || []);
      setHistory(data.history || []);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (code, nextEnabled) => {
    setBusy(code);
    try {
      await axios.put(`${API_URL}/investor-accounts/${accountId}/compliances/${code}`, {
        enabled: nextEnabled,
        change_remarks: remark.trim(),
      });
      setConfirming(null);
      setRemark('');
      await load();
    } catch (e) {
      alert(`Failed to update: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <p className="text-gray-500 text-sm">Loading compliance rules…</p>;

  return (
    <div className="space-y-4" data-testid="tab-content-compliance">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <div className="flex items-start gap-2">
          <ShieldCheck size={18} className="mt-0.5 shrink-0" />
          <div>
            Event-based compliance checks — enabling a rule triggers an
            automated email to the AMC&apos;s <b>Compliance Recipients</b>
            (managed under User Management → Compliance Recipients).
            All rules default to <b>OFF</b>. <b>Issue Schedule K-1</b> is a flag only (no emails) — flagged accounts appear in the Schedule K-1 report.
          </div>
        </div>
      </div>

      {rules.length === 0 ? (
        <p className="text-gray-500 text-sm">No compliance rules registered.</p>
      ) : rules.map((r) => (
        <div key={r.code} className="border rounded-lg p-4 bg-white" data-testid={`compliance-rule-${r.code}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-900">{r.label}</p>
                {r.enabled ? (
                  <span className="inline-block bg-green-100 text-green-800 text-[10px] px-2 py-0.5 rounded-full font-medium" data-testid={`compliance-badge-${r.code}`}>ON</span>
                ) : (
                  <span className="inline-block bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 rounded-full font-medium" data-testid={`compliance-badge-${r.code}`}>OFF</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Event Trigger: {r.event}</p>
              <p className="text-xs text-gray-700 mt-1">{r.description}</p>
              <details className="mt-2">
                <summary className="text-xs text-blue-700 cursor-pointer">Regulatory provision</summary>
                <p className="text-[11px] text-gray-600 bg-amber-50 border-l-2 border-amber-300 p-2 mt-1">{r.regulatory_text}</p>
              </details>
              {r.updated_by && (
                <p className="text-[10px] text-gray-400 mt-2">
                  Last changed by {r.updated_by}
                  {r.updated_at ? ` on ${new Date(r.updated_at).toLocaleString()}` : ''}
                  {(() => {
                    // Surface the reason from the most recent history
                    // entry for THIS rule so users can see it at a
                    // glance without expanding the full history panel.
                    const latest = [...history].reverse().find((h) => h.code === r.code);
                    return latest?.change_remarks
                      ? <span className="block italic text-amber-700 mt-0.5" data-testid={`compliance-latest-remark-${r.code}`}>Reason: {latest.change_remarks}</span>
                      : null;
                  })()}
                </p>
              )}
            </div>
            <div className="shrink-0">
              {confirming === r.code ? (
                <div className="w-72 border border-amber-300 bg-amber-50 rounded p-2">
                  <p className="text-xs font-medium text-amber-900 mb-1">
                    {r.enabled ? 'Disable this rule?' : 'Enable this rule?'}
                  </p>
                  <textarea
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    rows={2}
                    placeholder="Optional reason for change history"
                    className="w-full border border-amber-300 rounded px-2 py-1 text-xs bg-white"
                    data-testid={`compliance-remark-${r.code}`}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                      onClick={() => { setConfirming(null); setRemark(''); }}
                      data-testid={`compliance-cancel-${r.code}`}
                    >Cancel</button>
                    <button
                      className={`text-xs px-2 py-1 rounded text-white ${r.enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} disabled:opacity-50`}
                      disabled={busy === r.code}
                      onClick={() => toggle(r.code, !r.enabled)}
                      data-testid={`compliance-confirm-${r.code}`}
                    >
                      {busy === r.code ? 'Saving…' : r.enabled ? 'Turn OFF' : 'Turn ON'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  role="switch"
                  aria-checked={r.enabled}
                  onClick={() => setConfirming(r.code)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${r.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                  data-testid={`compliance-toggle-${r.code}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${r.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      {history.length > 0 && (
        <div className="border-t pt-4 mt-6" data-testid="compliance-history-panel">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-blue-700"
            data-testid="compliance-history-toggle"
          >
            {historyOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <HistoryIcon size={16} />
            Change History ({history.length})
          </button>
          {historyOpen && (
            <div className="mt-3 space-y-2">
              {[...history].reverse().map((h, i) => {
                const rule = rules.find((rr) => rr.code === h.code);
                const label = rule?.label || h.code;
                return (
                  <div key={i} className="bg-gray-50 border rounded p-3 text-xs" data-testid={`compliance-history-entry-${i}`}>
                    <p>
                      <span className="font-medium">{h.changed_by}</span>
                      <span className="text-gray-400 ml-1">on {new Date(h.changed_at).toLocaleString()}</span>
                    </p>
                    <p className="mt-0.5">
                      <span className="text-gray-600">{label} — </span>
                      <span className={`font-medium ${h.new_enabled ? 'text-green-700' : 'text-gray-700'}`}>
                        {h.previous_enabled ? 'ON' : 'OFF'} → {h.new_enabled ? 'ON' : 'OFF'}
                      </span>
                    </p>
                    {h.change_remarks && (
                      <p className="italic text-amber-700 mt-1" data-testid={`compliance-history-remark-${i}`}>
                        Reason: {h.change_remarks}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {events.length > 0 && (
        <div className="border-t pt-4 mt-6" data-testid="compliance-events-panel">
          <button
            type="button"
            onClick={() => setEventsOpen((v) => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-blue-700"
            data-testid="compliance-events-toggle"
          >
            {eventsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <HistoryIcon size={16} />
            Compliance Events ({events.length})
          </button>
          {eventsOpen && (
            <div className="mt-3 space-y-2">
              {[...events].reverse().map((e, i) => (
                <div key={i} className="bg-gray-50 border rounded p-3 text-xs" data-testid={`compliance-event-${i}`}>
                  <p><b>{e.drawdown_number}</b> · Amount: {e.receipt_amount} · Receipt date: {e.receipt_date}</p>
                  <p className="text-gray-500 mt-0.5">Fired at {new Date(e.fired_at).toLocaleString()}</p>
                  <p className="text-green-700 mt-0.5">Sent to: {(e.recipients_sent || []).join(', ') || '—'}</p>
                  {(e.recipients_failed || []).length > 0 && (
                    <p className="text-red-600 mt-0.5">
                      <AlertTriangle size={12} className="inline mr-1" />
                      Failed: {e.recipients_failed.join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComplianceTab;
